import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AuthService } from '_common/services';
import { useSelector, useDispatch, useEffectOnUpdate, usePublicProfiles } from '_common/hooks';
import { useFilterSelector } from '_common/components/Filters/FilterSlice';

import { EmptyFilteredState } from '_common/components';
import { listObjects } from '_common/components/Table/TableSlice';
import { openAndUpdateModal, openModal, updateModal } from '_common/modals/ModalsSlice';

import { LOADING_STATE } from 'Settings/pages/TenantSettingsPage/Users/constants';

import { ThunksTable, Cell, type TableColumn, type TableRow } from '_common/components/Table';
import EmailAddressCell from './EmailAddressCell';
import NameCell from './NameCell';
import DateCell from './DateCell';

import style from './Table.module.scss';

const IDENTITY: Table.Identity = 'userManagement/users';

const Table = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { selectFilterParams } = useFilterSelector();

  const searchParams = useSelector((state) => state.userManagement.filters);
  const filterParams = useSelector((state) =>
    selectFilterParams(state, state.filters.tenantSettings_users),
  );
  const userIds = useSelector((state) => state.table.identity['userManagement/users'].list);
  const users = useSelector((state) => state.userManagement.users);
  const { profiles } = usePublicProfiles(userIds);
  const contentLoading = useSelector((state) => state.table.contentLoading);

  //#region Table object listing handlers
  const fetchObjects = useCallback(
    async (parameters: any) => {
      const response = await new AuthService().getUsersList({
        ...parameters,
        filter_fields: [...searchParams.filter_fields, ...filterParams.filter_fields],
        filter_values: [...searchParams.filter_values, ...filterParams.filter_values],
      });

      return {
        ...response,
        data: {
          //@ts-expect-error Missing endpoint type "/api/authority/user/list"
          nodes: response.data.items.map((item) => {
            return { ...item, id: `${item.id}` };
          }),
          //@ts-expect-error Missing endpoint type "/api/authority/user/list"
          nodes_length: response.data.nodes_length,
          dataStorage: 'tenantUsers',
        },
      };
    },
    [searchParams, filterParams],
  );

  useEffect(() => {
    dispatch(
      listObjects({
        identity: IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  useEffectOnUpdate(() => {
    dispatch(
      listObjects({
        identity: IDENTITY,
        fetch: fetchObjects,
        cause: 'FILTER',
        request: {
          offset: 0,
          filter_fields: [...searchParams.filter_fields, ...filterParams.filter_fields],
          filter_values: [...searchParams.filter_values, ...filterParams.filter_values],
        },
      }),
    );
  }, [searchParams, filterParams]);
  //#endregion

  const handleRemoveUser = (user: UserPublicProfileExtended) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          width: '52.5rem',
          title: 'DELETE_SOMETHING_QUOTED',
          titleValues: { label: user.email },
          message: `REMOVING_USER_FROM_TENANT_DELETES_ALL_ACTIVITY_CONFIRM`,
          messageValues: { email: user.email },
          confirmButtonTextId: 'groups.removeUser',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          cancelButtonShow: true,
          actionCode: 'removeUserFromTenant',
          actionValue: { user },
        },
      }),
    );
  };

  const handleDeactivateUser = (user: UserPublicProfileExtended) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          width: '60rem',
          title: 'DEACTIVATE_USER',
          message: `DEACTIVATE_USER_FROM_TENANT`,
          messageValues: { firstName: user.first_name, lastName: user.last_name },
          confirmButtonTextId: 'DEACTIVATE_USER',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          cancelButtonShow: true,
          actionCode: 'editUserActiveState',
          actionValue: { user, active: false },
        },
      }),
    );
  };

  const handleActivateUser = (user: UserPublicProfileExtended) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          width: '60rem',
          title: 'ACTIVATE_USER',
          message: `ACTIVATE_USER_FROM_TENANT`,
          confirmButtonTextId: 'ACTIVATE_USER',
          confirmButtonType: 'primary',
          cancelButtonTextId: 'global.cancel',
          cancelButtonShow: true,
          actionCode: 'editUserActiveState',
          actionValue: { user, active: true },
        },
      }),
    );
  };

  const handleEditUser = (user: UserPublicProfileExtended) => {
    dispatch(
      openAndUpdateModal({
        modal: 'UserManagementFormModal',
        data: {
          title: 'EDIT_USER_INFORMATION',
          submit: 'SAVE_CHANGES',
          user,
        },
      }),
    );
  };

  const openTokenHistory = (user: UserPublicProfileExtended) => {
    dispatch(updateModal({ modal: 'UserTokensModal', data: { user } }));
    dispatch(openModal('UserTokensModal'));
  };

  const columns = useMemo<TableColumn<UserPublicProfileExtended>[]>(() => {
    return [
      {
        id: 'email',
        accessorKey: 'email',
        header: intl.formatMessage({ id: 'EMAIL_ADDRESS' }),
        cell: (info) => {
          const user = info.row.original;
          return (
            <Cell testId={`${user.id}-email-column`}>
              <EmailAddressCell user={user} userProfile={profiles[user.id] ?? user} />
            </Cell>
          );
        },
      },
      {
        id: 'first_name',
        accessorKey: 'first_name',
        size: 160,
        header: intl.formatMessage({ id: 'FIRST_NAME' }),
        cell: (info) => {
          const user = info.row.original;
          return (
            <Cell testId={`${user.id}-first-name-column`}>
              <NameCell firstName user={user} />
            </Cell>
          );
        },
      },
      {
        id: 'last_name',
        accessorKey: 'last_name',
        size: 160,
        header: intl.formatMessage({ id: 'LAST_NAME' }),
        cell: (info) => {
          const user = info.row.original;
          return (
            <Cell testId={`${user.id}-last-name-column`}>
              <NameCell user={user} />
            </Cell>
          );
        },
      },
      {
        id: 'date_joined',
        accessorKey: 'date_joined',
        size: 160,
        header: intl.formatMessage({ id: 'storage.browserHeader.dateModified' }),
        cell: (info) => {
          const user = info.row.original;
          return (
            <Cell testId={`${user.id}-date-modified-column`}>
              <DateCell user={user} />
            </Cell>
          );
        },
      },
      {
        id: 'admin',
        accessorFn: (user) => user.is_admin || user.is_superuser,
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'ADMINISTRATOR' }),
        cell: (info) => {
          const user = info.row.original;
          return (
            <Cell testId={`${user.id}-admin-column`}>
              <Cell.Boolean
                value={user?.is_admin || user?.is_superuser}
                testId={`${user.id}-admin`}
              />
            </Cell>
          );
        },
      },
    ];
  }, [profiles]);

  const rowsData = useMemo(() => {
    return userIds.map<TableRow<UserPublicProfileExtended>>((userId) => {
      const user = users[userId];
      const isAdmin = user.is_admin || user.is_superuser;

      const suffixActions: NonNullable<TableRow['suffix']>['actions'] = [
        {
          label: intl.formatMessage({ id: 'EDIT_USER_INFORMATION' }),
          onClick: () => handleEditUser(user),
        },
        {
          label: intl.formatMessage({ id: 'SEE_USER_TOKEN_HISTORY' }),
          onClick: () => openTokenHistory(user),
        },
      ];

      if (!isAdmin) {
        if (user.is_active) {
          suffixActions.push({
            label: intl.formatMessage({ id: 'DEACTIVATE_USER' }),
            onClick: () => handleDeactivateUser(user),
            disabled: isAdmin || !user.is_active,
          });
        } else {
          suffixActions.push({
            label: intl.formatMessage({ id: 'ACTIVATE_USER' }),
            onClick: () => handleActivateUser(user),
            disabled: isAdmin || user.is_active,
          });
        }
        suffixActions.push({
          label: (
            <div className={`${style.danger}`}>
              {intl.formatMessage({ id: 'groups.removeUser' })}
            </div>
          ),
          onClick: () => handleRemoveUser(user),
          disabled: isAdmin,
        });
      }

      return {
        ...user,
        suffix: {
          actions: suffixActions,
        },
      };
    });
  }, [users]);

  if (
    userIds.length === 0 &&
    (searchParams.filter_values?.length > 0 || filterParams.filter_fields?.length > 0)
  ) {
    return (
      <div className={`${style.center} ${style.empty}`}>
        <EmptyFilteredState
          testId="tenant-settings-users"
          message={{
            id:
              searchParams.filter_values?.length > 0
                ? 'NO_SEARCH_RESULTS_FOUND_FOR_USERS'
                : 'NO_FILTER_RESULTS_FOUND_FOR_USERS',
            values: { br: <br /> },
          }}
        />
      </div>
    );
  }

  return (
    <div className={style.list}>
      <ThunksTable
        identity={IDENTITY}
        options={{ columns, data: rowsData }}
        lazyLoading
        fetchObjects={fetchObjects}
        selectable={false}
        renderFooter={() => null}
        loadingLabel={intl.formatMessage({
          id: contentLoading ? LOADING_STATE[contentLoading] : LOADING_STATE.INITIAL,
        })}
        testId="users"
      />
    </div>
  );
};

export default Table;
